// MACHINERY V2 (Stroje) — UI state slice (M2.0 foundation).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §2.3.
//
// Owns ONLY v2 UI state. Every data slice is REUSED UNCHANGED
// (customerMachinery, machineCatalog, customerServiceReports,
// customerMachine, customerMachineDocuments, machineGroups) — that reuse is
// what preserves data parity, so nothing here duplicates or mirrors machine
// data, and no existing slice's shape is touched.
//
// Persistence mirrors midasV3Slice / settingsV2UiSlice: the palette and the
// filter-rail collapse are UI preferences persisted to localStorage (non-auth
// keys, read at module scope — no network, no auth state). The transient
// view/filter/period selections are deliberately NOT persisted; they reset per
// session like the live machinery grid's filters do.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// TYPE-ONLY import: the 9-state union, so a filter code that is not one of the
// nine cannot be stored. M2.0 kept this slice on string[] to avoid a FORWARD
// dependency on M2.1; M2.1 has shipped, so the dependency now points backwards
// at a contract that exists, and `import type` keeps it out of the runtime
// bundle graph. (M2.2 decision.)
import type { MachineryV2CardState } from "./fleet/cardStates";

// ============================================================
// Types
// ============================================================

/** Active palette — rendered as the root class `p-claude` | `p-machineid`. */
export type MachineryV2Palette = "claude" | "machineid";

/** Fleet view size. V1 = big cards (3-up), V2 = grouped rows. The dropped
 *  third "compact" card variant (§9 D4) has no representation here on purpose. */
export type MachineryV2FleetView = "V1" | "V2";

/** Fleet section dimension (§9 D4 carries group-by beyond type). In Fleet V2
 *  every one of these renders through the SAME FleetGroupSection the designer
 *  drew for type-grouping — D4's consistency rule, no new section design. */
export type MachineryV2GroupBy = "type" | "location" | "line" | "department" | "all";

/** Live signal-ledger period pills (§8 M2.10):
 *  hour = Posledná hodina · shift = Táto zmena · day = Dnes ·
 *  week = Tento týždeň · total = Celkovo. Each is a REAL rollup window —
 *  never a scaled projection of another window. */
export type MachineryV2LedgerPeriod =
  | "hour"
  | "shift"
  | "day"
  | "week"
  | "total";

interface MachineryV2UiState {
  /** Active palette class suffix — `.p-claude` (default) or `.p-machineid`. */
  palette: MachineryV2Palette;
  /** Fleet view toggle (V1 big cards | V2 grouped rows). */
  fleetView: MachineryV2FleetView;
  /** Selected fleet status filters. Empty array = no status filter (show all).
   *  TIGHTENED at M2.2 from `string[]` to the 9-state union: `cardStates.ts`
   *  now exists, so an unrepresentable filter code (which would silently match
   *  nothing and hide the whole fleet) is a compile error instead. */
  fleetStatusFilters: MachineryV2CardState[];
  /** Machine-type filter (machine type id) — null = all types. */
  fleetTypeFilter: string | null;
  /** Location filter (location id) — null = all locations. Carried feature
   *  per §9 D4. */
  fleetLocationFilter: string | null;
  /**
   * >>> THE SINGLE GROUPING KEY FOR v2 (M2.3 ruling (i)). <<<
   * CLIENT-side. useFleetPageData sections the already-filtered base set by
   * this, once, and BOTH views render that same map — so V1's cards and V2's
   * rows can never section the same fleet differently, and changing the
   * dimension never re-fetches. `MachinesQuery.groupBy` is still SENT, but it
   * only shapes response keys that `flattenGroups` immediately flattens away;
   * it decides nothing the user sees.
   *
   * SEEDING — what it does and does NOT guarantee. `readGroupBy()` runs ONCE,
   * at module load, reading the live grid's localStorage key
   * `machinery_groupBy`. That gives a first-visit default matching whatever the
   * user last chose on /machinery. It is NOT an ongoing sync: the legacy key is
   * re-read on every mount for the (now display-inert) query param, so changing
   * group-by on the live grid mid-session and client-side navigating to
   * /machinery-v2 leaves this value untouched. That divergence is INTENDED —
   * v2 owns its own grouping, and v2 NEVER writes `machinery_groupBy` back, so
   * the live grid's grouping stays unreachable from here. Inside v2 there is
   * no divergence at all: both views read this field and nothing else.
   */
  fleetGroupBy: MachineryV2GroupBy;
  /** Live ledger period pill selection. */
  ledgerPeriod: MachineryV2LedgerPeriod;
  /** Filter-rail collapsed state (fleet + catalog rails share it). */
  railCollapsed: boolean;
}

// ============================================================
// localStorage hydration (mirrors settingsV2UiSlice.ts lines 53-80)
// ============================================================

const LS_PALETTE_KEY = "machinery-v2-palette";
/** Read-only seed: the LIVE grid owns this key. v2 never writes it, so
 *  toggling the V2 grouping cannot change what /machinery does. */
const LS_LEGACY_GROUP_BY_KEY = "machinery_groupBy";
const LS_RAIL_COLLAPSED_KEY = "machinery-v2-rail-collapsed";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readPalette = (): MachineryV2Palette => {
  const ls = safeLocalStorage();
  if (!ls) return "claude";
  const raw = ls.getItem(LS_PALETTE_KEY);
  if (raw === "claude" || raw === "machineid") return raw;
  return "claude";
};

const writePalette = (palette: MachineryV2Palette): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_PALETTE_KEY, palette);
  } catch {
    // localStorage may throw in private mode / on quota; not fatal for v2 UX.
  }
};

const GROUP_BY_VALUES: MachineryV2GroupBy[] = [
  "type", "location", "line", "department", "all",
];

const readGroupBy = (): MachineryV2GroupBy => {
  const ls = safeLocalStorage();
  if (!ls) return "type";
  const raw = ls.getItem(LS_LEGACY_GROUP_BY_KEY);
  return GROUP_BY_VALUES.indexOf(raw as MachineryV2GroupBy) >= 0
    ? (raw as MachineryV2GroupBy)
    : "type";
};

const readRailCollapsed = (): boolean => {
  const ls = safeLocalStorage();
  if (!ls) return false;
  return ls.getItem(LS_RAIL_COLLAPSED_KEY) === "true";
};

const writeRailCollapsed = (collapsed: boolean): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_RAIL_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Same rationale as writePalette.
  }
};

// ============================================================
// Slice
// ============================================================

const initialState: MachineryV2UiState = {
  palette: readPalette(),
  fleetView: "V1",
  fleetStatusFilters: [],
  fleetTypeFilter: null,
  fleetLocationFilter: null,
  fleetGroupBy: readGroupBy(),
  ledgerPeriod: "hour",
  railCollapsed: readRailCollapsed(),
};

const machineryV2UiSlice = createSlice({
  name: "machineryV2Ui",
  initialState,
  reducers: {
    setPalette: (state, action: PayloadAction<MachineryV2Palette>) => {
      state.palette = action.payload;
      writePalette(action.payload);
    },
    togglePalette: (state) => {
      const next: MachineryV2Palette =
        state.palette === "claude" ? "machineid" : "claude";
      state.palette = next;
      writePalette(next);
    },
    setFleetView: (state, action: PayloadAction<MachineryV2FleetView>) => {
      state.fleetView = action.payload;
    },
    setFleetStatusFilters: (
      state,
      action: PayloadAction<MachineryV2CardState[]>
    ) => {
      state.fleetStatusFilters = action.payload;
    },
    toggleFleetStatusFilter: (
      state,
      action: PayloadAction<MachineryV2CardState>
    ) => {
      const code = action.payload;
      state.fleetStatusFilters = state.fleetStatusFilters.includes(code)
        ? state.fleetStatusFilters.filter((c) => c !== code)
        : [...state.fleetStatusFilters, code];
    },
    setFleetTypeFilter: (state, action: PayloadAction<string | null>) => {
      state.fleetTypeFilter = action.payload;
    },
    setFleetLocationFilter: (state, action: PayloadAction<string | null>) => {
      state.fleetLocationFilter = action.payload;
    },
    setFleetGroupBy: (state, action: PayloadAction<MachineryV2GroupBy>) => {
      // Session-only, exactly like the other fleet selections. Persisting it
      // would mean writing `machinery_groupBy`, which the LIVE grid reads —
      // v2 must not be able to change how /machinery groups.
      state.fleetGroupBy = action.payload;
    },
    clearFleetFilters: (state) => {
      state.fleetStatusFilters = [];
      state.fleetTypeFilter = null;
      state.fleetLocationFilter = null;
    },
    setLedgerPeriod: (
      state,
      action: PayloadAction<MachineryV2LedgerPeriod>
    ) => {
      state.ledgerPeriod = action.payload;
    },
    setRailCollapsed: (state, action: PayloadAction<boolean>) => {
      state.railCollapsed = action.payload;
      writeRailCollapsed(action.payload);
    },
    toggleRailCollapsed: (state) => {
      state.railCollapsed = !state.railCollapsed;
      writeRailCollapsed(state.railCollapsed);
    },
  },
});

export const {
  setPalette,
  togglePalette,
  setFleetView,
  setFleetStatusFilters,
  toggleFleetStatusFilter,
  setFleetTypeFilter,
  setFleetLocationFilter,
  setFleetGroupBy,
  clearFleetFilters,
  setLedgerPeriod,
  setRailCollapsed,
  toggleRailCollapsed,
} = machineryV2UiSlice.actions;

export default machineryV2UiSlice.reducer;
